import { NgxLoggerLevel } from 'ngx-logger';
import { environment } from './environment';

type RuntimeConfig = typeof environment;
type RuntimeConfigPatch = DeepPartial<RuntimeConfig>;

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

const CONFIG_URL = '/assets/conf.json';

export async function loadRuntimeConfig(): Promise<void> {
  try {
    const response = await fetch(`${CONFIG_URL}?t=${Date.now()}`, {
      cache: 'no-store'
    });

    if (response.status === 404) {
      return;
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const config = (await response.json()) as RuntimeConfigPatch;
    normalizeLoggerLevel(config.logger, 'loglevel');
    normalizeLoggerLevel(config.logger, 'serverLogLevel');
    normalizeRedirectUri(config.authConfig);
    mergeConfig(environment, config);
  } catch (error) {
    console.warn(`Runtime config ${CONFIG_URL} non caricata, uso environment compilato.`, error);
  }
}

function mergeConfig<T extends Record<string, any>>(target: T, source: DeepPartial<T>): T {
  Object.entries(source).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (isPlainObject(value) && isPlainObject(target[key])) {
      mergeConfig(target[key], value);
      return;
    }

    target[key as keyof T] = value as T[keyof T];
  });

  return target;
}

function isPlainObject(value: unknown): value is Record<string, any> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function normalizeLoggerLevel(logger: RuntimeConfigPatch['logger'], key: 'loglevel' | 'serverLogLevel'): void {
  const value = logger?.[key];

  if (typeof value === 'string') {
    const loggerLevel = NgxLoggerLevel[value as keyof typeof NgxLoggerLevel];

    if (typeof loggerLevel === 'number') {
      logger![key] = loggerLevel as RuntimeConfig['logger'][typeof key];
    }
  }
}

function normalizeRedirectUri(authConfig: RuntimeConfigPatch['authConfig']): void {
  if (typeof authConfig?.redirectUri === 'string' && authConfig.redirectUri.startsWith('/')) {
    authConfig.redirectUri = `${window.location.origin}${authConfig.redirectUri}`;
  }
}
